import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import subMonths from 'date-fns/subMonths';
import subWeeks from 'date-fns/subWeeks';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  REPORTING_GRAINS,
  REPORTING_TIMEZONES,
  ReportingGrain,
  ReportingGroupLevel,
  ReportingTimezone,
  WeeklyAdoptionLevelResponse,
  countGrainBuckets,
} from '@eluve/api-contract';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Content,
  Header,
  Heading,
  Icon,
  LoadingIcon,
  LoadingIndicator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from '@eluve/components';
import { useSession, useTenantIdFromParams } from '@eluve/session-helpers';

import {
  AdoptionTable,
  ChildrenState,
  ParentScope,
  TableSort,
} from './AdoptionTable';
import { AggregateTable } from './AggregateTable';
import { CountBadge } from './CountBadge';
import { ExportButton } from './ExportButton';
import { FilterBar, FilterSelection, FilterType } from './FilterBar';
import { GroupByBar } from './GroupByBar';
import { LatencyTrendCharts } from './LatencyTrendChart';
import {
  DEFAULT_RANGE_PRESET,
  MAX_DAY_GRAIN_SPAN_DAYS,
  RangePreset,
  TimeControls,
  exceedsWeekGrainCap,
  isNamedRangePreset,
  parseRangePreset,
} from './TimeControls';
import { TrendCharts } from './TrendChart';
import { GROUP_DIMS, GROUP_DIM_LABELS, GroupDim } from './groupDims';
import { formatSeconds } from './latency';
import { DEFAULT_METRIC, isBucketIncomplete } from './metrics';

const DAY_MS = 24 * 60 * 60 * 1000;

const isoDaysAgo = (days: number): string =>
  new Date(Date.now() - days * DAY_MS).toISOString().slice(0, 10);

/** Grouping applied when the URL carries no groupBy at all. */
const DEFAULT_GROUP_BY: GroupDim[] = ['location', 'specialty'];
/** URL sentinel for "explicitly no grouping" (distinct from absent). */
const GROUP_BY_NONE = 'none';

/** Timezone choice persists across sessions; the URL still wins. */
const TZ_COOKIE = 'eluve-reporting-tz';

const readTimezoneCookie = (): ReportingTimezone | null => {
  const value = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${TZ_COOKIE}=`))
    ?.slice(TZ_COOKIE.length + 1);
  return value &&
    (REPORTING_TIMEZONES as readonly string[]).includes(
      decodeURIComponent(value),
    )
    ? (decodeURIComponent(value) as ReportingTimezone)
    : null;
};

const writeTimezoneCookie = (timezone: ReportingTimezone) => {
  document.cookie = `${TZ_COOKIE}=${encodeURIComponent(timezone)}; path=/; max-age=31536000; samesite=lax`;
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
}> = ({ label, value, detail }) => (
  <Card>
    <CardContent className="p-3">
      <Stack gap={0.5}>
        <Text size="sm" color="tertiary">
          {label}
        </Text>
        {value}
        {detail}
      </Stack>
    </CardContent>
  </Card>
);

const Delta: React.FC<{
  current: number;
  previous: number;
  suffix?: string;
}> = ({ current, previous, suffix = '' }) => {
  const delta = current - previous;
  const positive = delta >= 0;
  // Point deltas (suffix set) are already relative — a % change on top of
  // them reads as a duplicate
  const pctChange =
    !suffix && previous !== 0
      ? Math.round((delta / Math.abs(previous)) * 100)
      : null;
  return (
    <Text
      size="sm"
      weight="bold"
      className={
        positive ? 'text-text-positive-subtle' : 'text-text-negative-subtle'
      }
    >
      {positive ? '▲' : '▼'} {positive ? '+' : ''}
      {delta}
      {suffix}
      {pctChange != null ? ` (${positive ? '+' : ''}${pctChange}%)` : ''}
    </Text>
  );
};

/** Compact relative age for the data-as-of chip: 39m / 2h / 3d ago. */
const relativeAsOf = (iso: string): string => {
  const minutes = Math.max(
    0,
    Math.round((Date.now() - Date.parse(iso)) / 60_000),
  );
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${Math.round(hours / 24)}d ago`;
};

const bucketLabel = (bucketStart: string, grain: ReportingGrain): string => {
  const date = new Date(`${bucketStart}T00:00:00`);
  if (grain === 'month') {
    return format(date, 'MMMM yyyy');
  }
  return `${grain === 'week' ? 'Week of ' : ''}${format(date, 'MMM d')}`;
};

/** "Week of Jun 29 vs Jun 22" — the buckets a tile's delta compares. */
const bucketCompareLabel = (
  current: string,
  previous: string | null | undefined,
  grain: ReportingGrain,
): string => {
  const label = bucketLabel(current, grain);
  if (!previous) {
    return label;
  }
  const previousDate = new Date(`${previous}T00:00:00`);
  return `${label} vs ${format(
    previousDate,
    grain === 'month' ? 'MMMM yyyy' : 'MMM d',
  )}`;
};

type LoadState =
  | { status: 'loading' }
  | { status: 'preparing' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; data: WeeklyAdoptionLevelResponse };

export const ReportingDashboardPage: React.FC = () => {
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams()!;
  const { isEluveAdmin } = useSession();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  // Filters and grouping live in the URL so a shared link reproduces the view
  const [locationIdsParam, setLocationIdsParam] = useQueryState(
    'locationIds',
    parseAsArrayOf(parseAsString),
  );
  const [specialtiesParam, setSpecialtiesParam] = useQueryState(
    'specialties',
    parseAsArrayOf(parseAsString),
  );
  const [providerIdsParam, setProviderIdsParam] = useQueryState(
    'providerIds',
    parseAsArrayOf(parseAsString),
  );
  const [templateIdsParam, setTemplateIdsParam] = useQueryState(
    'templateIds',
    parseAsArrayOf(parseAsString),
  );
  const [microphonesParam, setMicrophonesParam] = useQueryState(
    'microphones',
    parseAsArrayOf(parseAsString),
  );
  // Column sort lives in the URL too so shared links reproduce the ordering
  const [sortParam, setSortParam] = useQueryState('sort', parseAsString);
  const [sortDirParam, setSortDirParam] = useQueryState('dir', parseAsString);
  const [groupByParam, setGroupByParam] = useQueryState(
    'groupBy',
    parseAsArrayOf(parseAsString),
  );
  const [rangeParam, setRangeParam] = useQueryState('range', parseAsString);
  const [fromParam, setFromParam] = useQueryState('from', parseAsString);
  const [toParam, setToParam] = useQueryState('to', parseAsString);
  const [tzParam, setTzParam] = useQueryState('tz', parseAsString);
  const [grainParam, setGrainParam] = useQueryState('grain', parseAsString);
  const [onboardedParam, setOnboardedParam] = useQueryState(
    'onboarded',
    parseAsString,
  );
  const onboardedOnly = onboardedParam !== 'false';
  const [viewParam, setViewParam] = useQueryState('view', parseAsString);
  const view: 'breakdown' | 'aggregate' =
    viewParam === 'breakdown' ? 'breakdown' : 'aggregate';
  // Page-level report: adoption funnel vs note-generation latency. Both are
  // served by the same fetch, so switching is instant. Latencies is
  // Eluve-admin-only: the derived value ignores the URL param for
  // non-admins (covers deep links even if the page ever opens up beyond
  // admins), and the effect below scrubs the hacked param from the URL.
  // The reporting API itself sits behind EluveAdminGuard either way.
  const [pageTabParam, setPageTabParam] = useQueryState('tab', parseAsString);
  const pageTab: 'performance' | 'latencies' =
    pageTabParam === 'latencies' && isEluveAdmin ? 'latencies' : 'performance';
  useEffect(() => {
    if (!isEluveAdmin && pageTabParam === 'latencies') {
      void setPageTabParam(null);
    }
  }, [isEluveAdmin, pageTabParam, setPageTabParam]);

  const preset: RangePreset =
    rangeParam === 'custom'
      ? 'custom'
      : isNamedRangePreset(rangeParam) || parseRangePreset(rangeParam)
        ? (rangeParam as RangePreset)
        : DEFAULT_RANGE_PRESET;
  const timezone: ReportingTimezone = (
    REPORTING_TIMEZONES as readonly string[]
  ).includes(tzParam ?? '')
    ? (tzParam as ReportingTimezone)
    : (readTimezoneCookie() ?? 'UTC');
  // The cookie holds the user's saved default — reflect it into the URL when
  // the link didn't specify one, so sharing the page keeps the same view
  useEffect(() => {
    if (!tzParam) {
      const saved = readTimezoneCookie();
      if (saved && saved !== 'UTC') {
        void setTzParam(saved);
      }
    }
    // Mount-only: later tz changes go through onTimezoneChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { fromIso, toIso } = useMemo(() => {
    const today = isoDaysAgo(0);
    if (preset === 'custom') {
      return {
        fromIso: fromParam ?? isoDaysAgo(48),
        toIso: toParam ?? today,
      };
    }
    if (isNamedRangePreset(preset)) {
      const now = new Date();
      const anchor =
        preset === 'last-week'
          ? subWeeks(now, 1)
          : preset === 'last-month'
            ? subMonths(now, 1)
            : now;
      const isWeek = preset === 'this-week' || preset === 'last-week';
      return {
        fromIso: format(
          isWeek
            ? startOfWeek(anchor, { weekStartsOn: 1 })
            : startOfMonth(anchor),
          'yyyy-MM-dd',
        ),
        toIso: format(
          isWeek ? endOfWeek(anchor, { weekStartsOn: 1 }) : endOfMonth(anchor),
          'yyyy-MM-dd',
        ),
      };
    }
    const parsed = parseRangePreset(preset)!;
    if (parsed.unit === 'd') {
      return { fromIso: isoDaysAgo(parsed.count - 1), toIso: today };
    }
    // Week/month presets snap to whole calendar periods, current one
    // included: Monday..Sunday and 1st..last day of month respectively.
    const now = new Date();
    if (parsed.unit === 'w') {
      return {
        fromIso: format(
          startOfWeek(subWeeks(now, parsed.count - 1), { weekStartsOn: 1 }),
          'yyyy-MM-dd',
        ),
        toIso: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      };
    }
    return {
      fromIso: format(
        startOfMonth(subMonths(now, parsed.count - 1)),
        'yyyy-MM-dd',
      ),
      toIso: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  }, [preset, fromParam, toParam]);
  const spanDays =
    Math.round((Date.parse(toIso) - Date.parse(fromIso)) / DAY_MS) + 1;

  // Day grain is only valid for short windows; fall back to week otherwise.
  // Without an explicit choice, short windows (≤7 days) default to Day.
  const requestedGrain: ReportingGrain = (
    REPORTING_GRAINS as readonly string[]
  ).includes(grainParam ?? '')
    ? (grainParam as ReportingGrain)
    : spanDays <= 7
      ? 'day'
      : 'week';
  // Buckets the server would emit at week grain, with `to` clamped to today
  // the way it clamps — this is what gates the Week grain, since counting days
  // would miss the Monday snap that can reach into one more bucket.
  const weekBuckets = useMemo(() => {
    const todayIso = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
    }).format(new Date());
    return countGrainBuckets(
      fromIso,
      toIso < todayIso ? toIso : todayIso,
      'week',
    );
  }, [fromIso, toIso, timezone]);

  // Each grain degrades to the next coarser one once the window outgrows it:
  // past the server's bucket cap the series would be clamped and the oldest
  // buckets dropped, so a coarser grain covering the whole window beats a
  // silently shortened one. Day can outgrow week too, hence the chain.
  const dayFallback: ReportingGrain =
    requestedGrain === 'day' && spanDays > MAX_DAY_GRAIN_SPAN_DAYS
      ? 'week'
      : requestedGrain;
  const grain: ReportingGrain =
    dayFallback === 'week' && exceedsWeekGrainCap(weekBuckets)
      ? 'month'
      : dayFallback;
  const locationIds = useMemo(() => locationIdsParam ?? [], [locationIdsParam]);
  const specialties = useMemo(() => specialtiesParam ?? [], [specialtiesParam]);
  const providerIds = useMemo(() => providerIdsParam ?? [], [providerIdsParam]);
  const templateIds = useMemo(() => templateIdsParam ?? [], [templateIdsParam]);
  const microphones = useMemo(() => microphonesParam ?? [], [microphonesParam]);
  // Comma-joined values as sent to the API (and used as effect deps)
  const toQuery = (values: string[]) =>
    values.length ? values.join(',') : undefined;
  const locationIdsQuery = toQuery(locationIds);
  const specialtiesQuery = toQuery(specialties);
  const providerIdsQuery = toQuery(providerIds);
  const templateIdsQuery = toQuery(templateIds);
  const microphonesQuery = toQuery(microphones);
  const filterSelection: FilterSelection = {
    location: locationIds,
    specialty: specialties,
    provider: providerIds,
    template: templateIds,
    microphone: microphones,
  };
  const filterSetters: Record<FilterType, (values: string[] | null) => void> = {
    location: setLocationIdsParam,
    specialty: setSpecialtiesParam,
    provider: setProviderIdsParam,
    template: setTemplateIdsParam,
    microphone: setMicrophonesParam,
  };
  const tableSort: TableSort | null = sortParam
    ? {
        key: sortParam,
        dir:
          sortDirParam === 'asc' || sortDirParam === 'desc'
            ? sortDirParam
            : sortParam === 'name'
              ? 'asc'
              : 'desc',
      }
    : null;
  const handleSortChange = (next: TableSort | null) => {
    void setSortParam(next?.key ?? null);
    void setSortDirParam(next?.dir ?? null);
  };
  const groupBy = useMemo(() => {
    // Absent → the default hierarchy; the 'none' sentinel → explicitly flat
    if (!groupByParam || groupByParam.length === 0) {
      return DEFAULT_GROUP_BY;
    }
    if (groupByParam.includes(GROUP_BY_NONE)) {
      return [];
    }
    return groupByParam.filter((dim): dim is GroupDim =>
      GROUP_DIMS.includes(dim as GroupDim),
    );
  }, [groupByParam]);
  const levels = useMemo<ReportingGroupLevel[]>(
    () => [...groupBy, 'provider'],
    [groupBy],
  );

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Non-null while a flatten is (or may be) running server-side: polls the
  // build status until flattenedAt moves past sinceAsOf, then refetches.
  const [awaitingBuild, setAwaitingBuild] = useState<{
    sinceAsOf: string | null;
  } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  // The toolbar is sticky; the table's column header stacks directly under
  // it, so track its rendered height (it wraps to multiple lines on mobile)
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  useEffect(() => {
    const element = toolbarRef.current;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setToolbarHeight(element.offsetHeight),
    );
    observer.observe(element);
    setToolbarHeight(element.offsetHeight);
    return () => observer.disconnect();
  }, [isEluveAdmin]);
  // Re-render every minute so the relative "data as of" label stays honest
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(
      () => setClockTick((tick) => tick + 1),
      60_000,
    );
    return () => clearInterval(interval);
  }, []);
  const [childrenByNode, setChildrenByNode] = useState<
    Map<string, ChildrenState>
  >(new Map());
  const [filterOptions, setFilterOptions] = useState<
    NonNullable<WeeklyAdoptionLevelResponse['filters']>
  >({
    locations: [],
    specialties: [],
    providers: [],
    templates: [],
    microphones: [],
  });
  const [totals, setTotals] =
    useState<WeeklyAdoptionLevelResponse['totals']>(undefined);

  // Root load: first grouping level with meta (summary + filter options).
  // Re-runs when filters or the first grouping level change; expansion state
  // is reset because node ids/scopes no longer apply.
  useEffect(() => {
    if (!isEluveAdmin) {
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    setExpanded(new Set());
    setChildrenByNode(new Map());

    const fetchRoot = async () => {
      try {
        const response = await apiClient.reporting.getWeeklyAdoptionLevel({
          params: { tenantId },
          query: {
            from: fromIso,
            to: toIso,
            timezone,
            grain,
            metric: DEFAULT_METRIC,
            locationIds: locationIdsQuery,
            specialties: specialtiesQuery,
            providerIds: providerIdsQuery,
            templateIds: templateIdsQuery,
            microphones: microphonesQuery,
            level: levels[0]!,
            includeMeta: 'true',
            onboardedOnly: onboardedOnly ? 'true' : 'false',
          },
        });
        if (cancelled) {
          return;
        }
        if (response.status === 200) {
          setState({ status: 'loaded', data: response.body });
          if (response.body.filters) {
            setFilterOptions(response.body.filters);
          }
          setTotals(response.body.totals);
        } else if (response.status === 409) {
          // Facts not built yet — a flatten was enqueued server-side; poll
          // the build status until it's ready, then this effect re-runs.
          setState({ status: 'preparing' });
          setAwaitingBuild({ sinceAsOf: null });
        } else if (response.status === 503) {
          setState({ status: 'error', message: response.body.message });
        } else {
          setState({
            status: 'error',
            message: `Unexpected response (${response.status})`,
          });
        }
      } catch {
        if (!cancelled) {
          setState({ status: 'error', message: 'Failed to load report data' });
        }
      }
    };

    void fetchRoot();
    return () => {
      cancelled = true;
    };
  }, [
    apiClient,
    tenantId,
    isEluveAdmin,
    locationIdsQuery,
    specialtiesQuery,
    providerIdsQuery,
    templateIdsQuery,
    microphonesQuery,
    levels,
    fromIso,
    toIso,
    timezone,
    grain,
    onboardedOnly,
    refreshNonce,
  ]);

  // Poll the flatten status while a build is expected, then refetch once
  // flattenedAt lands past the point we started waiting from.
  useEffect(() => {
    if (!awaitingBuild) {
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const response = await apiClient.reporting.getReportingStatus({
          params: { tenantId },
        });
        if (cancelled || response.status !== 200) {
          return;
        }
        const { state: buildState, flattenedAt } = response.body;
        if (
          buildState === 'ready' &&
          flattenedAt &&
          flattenedAt !== awaitingBuild.sinceAsOf
        ) {
          setAwaitingBuild(null);
          setRefreshNonce((nonce) => nonce + 1);
        }
      } catch {
        // transient — keep polling
      }
    };
    void check();
    const interval = setInterval(() => void check(), 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [awaitingBuild, apiClient, tenantId]);

  const handleRefresh = useCallback(() => {
    const currentAsOf =
      state.status === 'loaded' ? (state.data.dataAsOf ?? null) : null;
    void apiClient.reporting.refreshReporting({
      params: { tenantId },
      body: {},
    });
    setAwaitingBuild({ sinceAsOf: currentAsOf });
  }, [apiClient, tenantId, state]);

  const handleToggle = useCallback(
    (nodeId: string, depth: number, scope: ParentScope) => {
      setExpanded((previous) => {
        const next = new Set(previous);
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        return next;
      });

      const childLevel = levels[depth + 1];
      if (!childLevel || childrenByNode.has(nodeId)) {
        return;
      }

      setChildrenByNode((previous) =>
        new Map(previous).set(nodeId, { status: 'loading' }),
      );

      const fetchChildren = async () => {
        try {
          const response = await apiClient.reporting.getWeeklyAdoptionLevel({
            params: { tenantId },
            query: {
              from: fromIso,
              to: toIso,
              timezone,
              grain,
              metric: DEFAULT_METRIC,
              locationIds: locationIdsQuery,
              specialties: specialtiesQuery,
              providerIds: providerIdsQuery,
              templateIds: templateIdsQuery,
              microphones: microphonesQuery,
              level: childLevel,
              onboardedOnly: onboardedOnly ? 'true' : 'false',
              ...scope,
            },
          });
          setChildrenByNode((previous) =>
            new Map(previous).set(
              nodeId,
              response.status === 200
                ? { status: 'loaded', rows: response.body.rows }
                : { status: 'error' },
            ),
          );
        } catch {
          setChildrenByNode((previous) =>
            new Map(previous).set(nodeId, { status: 'error' }),
          );
        }
      };
      void fetchChildren();
    },
    [
      apiClient,
      tenantId,
      locationIdsQuery,
      specialtiesQuery,
      providerIdsQuery,
      templateIdsQuery,
      microphonesQuery,
      levels,
      childrenByNode,
      fromIso,
      toIso,
      timezone,
      grain,
      onboardedOnly,
    ],
  );

  const trendTotals = useMemo(
    () =>
      totals
        ?.filter((cell) => cell.counts)
        .map((cell) => ({
          bucketStart: cell.bucketStart,
          counts: cell.counts!,
          users: cell.users,
        })),
    [totals],
  );
  // Keep every bucket on the X axis: zero-fill buckets carry no latency,
  // so substitute an empty rollup instead of dropping the point
  const latencyTrendTotals = useMemo(() => {
    if (!totals?.some((cell) => cell.latency)) {
      return undefined;
    }
    const emptyStats = {
      n: 0,
      p50: null,
      p90: null,
      p95: null,
      p99: null,
      p100: null,
    };
    return totals.map((cell) => ({
      bucketStart: cell.bucketStart,
      latency: cell.latency ?? {
        noteWait: emptyStats,
        uploadDelay: emptyStats,
        avgWaitUpload: null,
        avgWaitTranscription: null,
        avgWaitGeneration: null,
      },
    }));
  }, [totals]);

  if (!isEluveAdmin) {
    return <Navigate to="../home" replace />;
  }

  const header = (
    <Header>
      <Stack direction="horizontal" gap={3} align="center" fullWidth={false}>
        <Heading size="2xl" weight="semibold">
          Reports
        </Heading>
        <Badge
          variant="secondary"
          className="border-border-warning-subtle bg-bg-warning-subtle text-text-warning-subtle"
          title="Only data from 2026 onward is available right now"
        >
          BETA
        </Badge>
      </Stack>
    </Header>
  );

  const dataAsOf =
    state.status === 'loaded' ? (state.data.dataAsOf ?? null) : null;
  const queryStats =
    state.status === 'loaded' ? (state.data.queryStats ?? null) : null;

  // One row on desktop (each bar owns a third and handles its own overflow),
  // stacked lines on mobile
  const toolbar = (
    <div
      ref={toolbarRef}
      className="sticky top-0 z-30 -mt-3 -mb-2 flex w-full flex-wrap items-center gap-2 bg-background pt-3 pb-2"
    >
      <FilterBar
        locations={filterOptions.locations}
        specialties={filterOptions.specialties}
        providers={filterOptions.providers ?? []}
        templates={filterOptions.templates ?? []}
        microphones={filterOptions.microphones ?? []}
        selected={filterSelection}
        onChange={(type, values) =>
          filterSetters[type](values.length ? values : null)
        }
      />
      <TimeControls
        preset={preset}
        customFrom={fromParam ?? undefined}
        customTo={toParam ?? undefined}
        timezone={timezone}
        grain={grain}
        spanDays={spanDays}
        weekBuckets={weekBuckets}
        onPresetChange={(next) => {
          setRangeParam(next === DEFAULT_RANGE_PRESET ? null : next);
          if (next !== 'custom') {
            setFromParam(null);
            setToParam(null);
          }
          // Short presets drop any explicit grain so the Day default applies
          if (
            parseRangePreset(next)?.unit === 'd' ||
            next === 'this-week' ||
            next === 'last-week'
          ) {
            setGrainParam(null);
          }
        }}
        onCustomRangeChange={(from, to) => {
          // One batched URL update (nuqs coalesces same-tick sets) → the
          // data effect re-runs exactly once, after both dates are chosen
          setFromParam(from);
          setToParam(to);
          setRangeParam('custom');
        }}
        onTimezoneChange={(next) => {
          writeTimezoneCookie(next);
          setTzParam(next === 'UTC' ? null : next);
        }}
        onGrainChange={(next) => setGrainParam(next)}
      />
      <div className="ml-auto flex items-center gap-2">
        {awaitingBuild != null ? (
          <LoadingIcon size="sm" />
        ) : (
          dataAsOf && (
            <Text size="sm" color="tertiary" className="whitespace-nowrap">
              {queryStats &&
                `${(queryStats.durationMs / 1000).toFixed(1)}s${queryStats.cached ? ' (cached)' : ''} · ${queryStats.rowCount.toLocaleString()} rows · `}
              Data as of {relativeAsOf(dataAsOf)}
            </Text>
          )
        )}
        <ExportButton
          tenantId={tenantId}
          query={{
            from: fromIso,
            to: toIso,
            timezone,
            grain,
            locationIds: locationIdsQuery,
            specialties: specialtiesQuery,
            providerIds: providerIdsQuery,
            templateIds: templateIdsQuery,
            microphones: microphonesQuery,
            groupBy: groupBy.length ? groupBy.join(',') : undefined,
            onboardedOnly: onboardedOnly ? 'true' : 'false',
          }}
          groupLabels={groupBy.map((dim) => GROUP_DIM_LABELS[dim])}
          disabled={state.status !== 'loaded'}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" aria-label="Report settings">
              <Icon name="Settings" size="sm" />
              {!onboardedOnly && <CountBadge count={1} />}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-3">
            <Stack gap={4}>
              <Stack direction="horizontal" gap={2.5} align="start">
                <Switch
                  checked={onboardedOnly}
                  onCheckedChange={(next) =>
                    setOnboardedParam(next ? null : 'false')
                  }
                  aria-label="Only count appointments after each user's onboarded date"
                />
                <Stack gap={0.5}>
                  <Text size="sm" weight="medium">
                    Onboarded Only
                  </Text>
                  <Text size="xs" color="tertiary">
                    Only count appointments on/after each user's onboarded date,
                    so imported backlog doesn't deflate the numbers.
                  </Text>
                </Stack>
              </Stack>
              <div className="flex items-center justify-between gap-3">
                <Stack gap={0.5}>
                  <Text size="sm" weight="medium">
                    Data Refresh
                  </Text>
                  <Text size="xs" color="tertiary">
                    {dataAsOf
                      ? `As of ${format(new Date(dataAsOf), 'MMM d, HH:mm')}`
                      : 'Not built yet'}
                  </Text>
                </Stack>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Refresh reporting data"
                  disabled={awaitingBuild != null}
                  onClick={handleRefresh}
                >
                  {awaitingBuild != null ? (
                    <LoadingIcon size="sm" />
                  ) : (
                    <Icon name="RefreshCw" size="sm" />
                  )}
                  Refresh
                </Button>
              </div>
            </Stack>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  const firstColumnLabel = levels
    .map((level) => (level === 'provider' ? 'User' : GROUP_DIM_LABELS[level]))
    .join(' / ');

  if (state.status !== 'loaded') {
    return (
      <>
        {header}
        <Content padded>
          <Stack gap={5}>
            {toolbar}
            <div className="flex min-h-[60vh] w-full grow items-center justify-center">
              {state.status === 'loading' ? (
                <LoadingIndicator size="xl" />
              ) : state.status === 'preparing' ? (
                <Card className="max-w-xl">
                  <CardContent className="p-6">
                    <Stack direction="horizontal" gap={3} align="center">
                      <LoadingIcon size="sm" />
                      <Stack gap={0.5}>
                        <Text weight="semibold">
                          Preparing your reporting data
                        </Text>
                        <Text size="sm" color="tertiary">
                          One-time setup — flattening this organization's
                          appointments, usually ready in a couple of minutes.
                        </Text>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ) : (
                <Text color="tertiary">{state.message}</Text>
              )}
            </div>
          </Stack>
        </Content>
      </>
    );
  }

  const { bucketStarts, rows } = state.data;

  // Tiles compare the latest COMPLETE bucket against the one before it —
  // the running bucket is never eligible
  const completeTotals = totals?.filter(
    (cell) => !isBucketIncomplete(cell.bucketStart, grain, timezone),
  );
  const currentTotal = completeTotals?.[completeTotals.length - 1];
  const previousTotal = completeTotals?.[completeTotals.length - 2];
  const pctOf = (numerator: number, denominator: number): number | null =>
    denominator > 0 ? Math.round((numerator / denominator) * 100) : null;
  const currentCounts = currentTotal?.counts;
  const previousCounts = previousTotal?.counts;
  const compareDetail = currentTotal && (
    <Text size="xs" color="tertiary">
      {bucketCompareLabel(
        currentTotal.bucketStart,
        previousTotal?.bucketStart,
        grain,
      )}
    </Text>
  );

  const currentLatency = currentTotal?.latency;
  const previousLatency = previousTotal?.latency;
  // First latency tile set — tracked variables to be refined as we learn
  // which cuts matter (per-stage percentiles, SLA-breach counts, ...)
  const latencyTiles =
    currentLatency && currentLatency.noteWait.n > 0 ? (
      <div className="grid w-full grid-cols-2 gap-3 md:grid-cols-4">
        {(
          [
            {
              label: 'Median note wait',
              current: currentLatency.noteWait.p50,
              previous: previousLatency?.noteWait.p50,
              measured: `${currentLatency.noteWait.n.toLocaleString()} appointments measured`,
            },
            {
              label: 'P90 note wait',
              current: currentLatency.noteWait.p90,
              previous: previousLatency?.noteWait.p90,
              measured: `${currentLatency.noteWait.n.toLocaleString()} appointments measured`,
            },
            {
              label: 'P99 note wait',
              current: currentLatency.noteWait.p99,
              previous: previousLatency?.noteWait.p99,
              measured: `${currentLatency.noteWait.n.toLocaleString()} appointments measured`,
            },
            {
              label: 'P99 upload delay',
              current: currentLatency.uploadDelay.p99,
              previous: previousLatency?.uploadDelay.p99,
              measured: `${currentLatency.uploadDelay.n.toLocaleString()} appointments measured`,
            },
          ] as const
        ).map((tile) => (
          <StatCard
            key={tile.label}
            label={tile.label}
            value={
              <Stack direction="horizontal" gap={2} align="baseline">
                <Heading size="xl" weight="bold">
                  {formatSeconds(tile.current)}
                </Heading>
                {tile.current != null && tile.previous != null && (
                  <Delta
                    current={tile.current}
                    previous={tile.previous}
                    suffix="s"
                  />
                )}
              </Stack>
            }
            detail={
              <Stack gap={0}>
                <Text size="xs" color="tertiary">
                  {tile.measured}
                </Text>
                {compareDetail}
              </Stack>
            }
          />
        ))}
      </div>
    ) : (
      <Text size="sm" color="tertiary">
        No latency data in the latest complete bucket yet — it appears after the
        next data refresh.
      </Text>
    );

  return (
    <>
      {header}
      <Content padded>
        <Stack gap={5}>
          {toolbar}
          {isEluveAdmin && (
            <Tabs
              value={pageTab}
              onValueChange={(next) =>
                setPageTabParam(next === 'latencies' ? 'latencies' : null)
              }
            >
              <TabsList>
                <TabsTrigger value="performance">Performance</TabsTrigger>
                <TabsTrigger value="latencies">Latencies</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          {pageTab === 'latencies' && latencyTiles}
          {pageTab === 'performance' && currentTotal && currentCounts && (
            <div className="grid w-full grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              {currentTotal.users && (
                <>
                  <StatCard
                    label="Users with scheduled appointments"
                    value={
                      <Stack direction="horizontal" gap={2} align="baseline">
                        <Heading size="xl" weight="bold">
                          {currentTotal.users.scheduled.toLocaleString()}
                        </Heading>
                        {previousTotal?.users && (
                          <Delta
                            current={currentTotal.users.scheduled}
                            previous={previousTotal.users.scheduled}
                          />
                        )}
                      </Stack>
                    }
                    detail={compareDetail}
                  />
                  <StatCard
                    label="Users with completed appointments"
                    value={
                      <Stack direction="horizontal" gap={2} align="baseline">
                        <Heading size="xl" weight="bold">
                          {currentTotal.users.completed.toLocaleString()}
                        </Heading>
                        {previousTotal?.users && (
                          <Delta
                            current={currentTotal.users.completed}
                            previous={previousTotal.users.completed}
                          />
                        )}
                      </Stack>
                    }
                    detail={compareDetail}
                  />
                </>
              )}
              <StatCard
                label="Scheduled appointments"
                value={
                  <Stack direction="horizontal" gap={2} align="baseline">
                    <Heading size="xl" weight="bold">
                      {currentCounts.total.toLocaleString()}
                    </Heading>
                    {previousCounts && (
                      <Delta
                        current={currentCounts.total}
                        previous={previousCounts.total}
                      />
                    )}
                  </Stack>
                }
                detail={compareDetail}
              />
              <StatCard
                label="Completed appointments"
                value={
                  <Stack direction="horizontal" gap={2} align="baseline">
                    <Heading size="xl" weight="bold">
                      {currentCounts.completed.toLocaleString()}
                    </Heading>
                    {previousCounts && (
                      <Delta
                        current={currentCounts.completed}
                        previous={previousCounts.completed}
                      />
                    )}
                  </Stack>
                }
                detail={compareDetail}
              />
              {(
                [
                  {
                    label: 'Completion %',
                    pct: pctOf(currentCounts.completed, currentCounts.total),
                    previousPct: previousCounts
                      ? pctOf(previousCounts.completed, previousCounts.total)
                      : null,
                  },
                  {
                    label: 'Note signed %',
                    pct: pctOf(currentCounts.signed, currentCounts.completed),
                    previousPct: previousCounts
                      ? pctOf(previousCounts.signed, previousCounts.completed)
                      : null,
                  },
                ] as const
              ).map((rate) => (
                <StatCard
                  key={rate.label}
                  label={rate.label}
                  value={
                    <Stack direction="horizontal" gap={2} align="baseline">
                      <Heading
                        size="xl"
                        weight="bold"
                        className="text-text-positive-subtle"
                      >
                        {rate.pct != null ? `${rate.pct}%` : '—'}
                      </Heading>
                      {rate.pct != null && rate.previousPct != null && (
                        <Delta
                          current={rate.pct}
                          previous={rate.previousPct}
                          suffix=" pts"
                        />
                      )}
                    </Stack>
                  }
                  detail={compareDetail}
                />
              ))}
            </div>
          )}
          {pageTab === 'performance' &&
            trendTotals &&
            trendTotals.length > 0 && (
              <TrendCharts
                totals={trendTotals}
                grain={grain}
                timezone={timezone}
              />
            )}
          {pageTab === 'latencies' &&
            latencyTrendTotals &&
            latencyTrendTotals.length > 0 && (
              <LatencyTrendCharts
                totals={latencyTrendTotals}
                grain={grain}
                timezone={timezone}
              />
            )}

          <Stack direction="horizontal" gap={2} align="center">
            <GroupByBar
              value={groupBy}
              onChange={(next) =>
                setGroupByParam(next.length ? next : [GROUP_BY_NONE])
              }
            />
            <Tabs
              value={view}
              onValueChange={(next) =>
                setViewParam(next === 'breakdown' ? 'breakdown' : null)
              }
            >
              {/* Match the h-8 sm buttons it sits beside (list defaults to h-10) */}
              <TabsList className="group-data-horizontal/tabs:h-8">
                <TabsTrigger value="aggregate">Aggregate</TabsTrigger>
                <TabsTrigger value="breakdown">By Time</TabsTrigger>
              </TabsList>
            </Tabs>
          </Stack>

          {view === 'aggregate' ? (
            <AggregateTable
              rows={rows}
              levels={levels}
              firstColumnLabel={firstColumnLabel}
              expanded={expanded}
              childrenByNode={childrenByNode}
              onToggle={handleToggle}
              sort={tableSort}
              onSortChange={handleSortChange}
              mode={pageTab === 'latencies' ? 'latency' : 'adoption'}
            />
          ) : (
            <AdoptionTable
              rows={rows}
              bucketStarts={bucketStarts}
              levels={levels}
              grain={grain}
              firstColumnLabel={firstColumnLabel}
              expanded={expanded}
              childrenByNode={childrenByNode}
              onToggle={handleToggle}
              sort={tableSort}
              onSortChange={handleSortChange}
              stickyTopOffset={toolbarHeight}
              cellMode={pageTab === 'latencies' ? 'latency' : 'adoption'}
            />
          )}
        </Stack>
      </Content>
    </>
  );
};
